import { ShieldCheck, ShieldAlert, Shield, User, CheckCircle2, XCircle, UserCheck, Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { User as AppUser } from '../types/user';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useNotification } from '../contexts/NotificationContext';
import { migrateOrphanReportsToAdmin } from '../utils/migrateOrphanReportsToAdmin';

interface Permission {
  label: string;
  admin: boolean;
  pasteur: boolean;
  responsable: boolean | 'partiel';
  user: boolean;
}

const PERMISSIONS: Permission[] = [
  {
    label: 'Accès au tableau de bord',
    admin: true, pasteur: true, responsable: true, user: true,
  },
  {
    label: 'Voir les rapports de département assignés',
    admin: true, pasteur: true, responsable: 'partiel', user: false,
  },
  {
    label: 'Créer un rapport',
    admin: true, pasteur: true, responsable: 'partiel', user: false,
  },
  {
    label: 'Modifier ses propres rapports',
    admin: true, pasteur: true, responsable: true, user: true,
  },
  {
    label: 'Modifier les rapports des autres',
    admin: true, pasteur: true, responsable: false, user: false,
  },
  {
    label: 'Supprimer ses propres rapports',
    admin: true, pasteur: true, responsable: true, user: true,
  },
  {
    label: 'Supprimer les rapports des autres',
    admin: true, pasteur: true, responsable: false, user: false,
  },
  {
    label: 'Gérer les utilisateurs',
    admin: true, pasteur: true, responsable: false, user: false,
  },
  {
    label: 'Gérer les rôles & permissions',
    admin: true, pasteur: true, responsable: false, user: false,
  },
  {
    label: 'Gérer les types de rencontre',
    admin: true, pasteur: true, responsable: false, user: false,
  },
  {
    label: 'Gérer les orateurs',
    admin: true, pasteur: true, responsable: false, user: false,
  },
  {
    label: 'Gestion des besoins',
    admin: true, pasteur: true, responsable: false, user: false,
  },
];

type Role = 'admin' | 'pasteur' | 'responsable' | 'user';

const ROLES: { key: Role; label: string; icon: React.ReactNode; color: string; description: string }[] = [
  {
    key: 'admin',
    label: 'Administrateur',
    icon: <ShieldAlert size={20} />,
    color: 'admin',
    description: 'Accès complet à toutes les fonctionnalités. Peut gérer les utilisateurs, les rôles et tous les rapports.',
  },
  {
    key: 'pasteur',
    label: 'Pasteur',
    icon: <ShieldCheck size={20} />,
    color: 'pasteur',
    description: 'Accès identique à l\'administrateur. Peut consulter et modifier tous les rapports et paramètres.',
  },
  {
    key: 'responsable',
    label: 'Responsable',
    icon: <Shield size={20} />,
    color: 'responsable',
    description: 'Accès limité aux départements qui lui sont assignés. Peut créer et modifier ses propres rapports.',
  },
  {
    key: 'user',
    label: 'Utilisateur',
    icon: <User size={20} />,
    color: 'user',
    description: 'Accès au tableau de bord uniquement. Peut modifier ses propres données de profil.',
  },
];

const COLOR_MAP: Record<string, string> = {
  admin:       'bg-[#00665C]/10 border-[#00665C]/25 text-[#00665C]',
  pasteur:     'bg-[#F2B636]/15 border-[#F2B636]/30 text-[#b97b10]',
  responsable: 'bg-[#00665C]/6  border-[#00665C]/15 text-[#00665C]',
  user:        'bg-gray-50 border-gray-200 text-gray-600',
};

const HEADER_COLOR: Record<string, string> = {
  admin:       'bg-[#00665C] text-white',
  pasteur:     'bg-[#F2B636] text-white',
  responsable: 'bg-[#00665C]/20 text-[#00665C]',
  user:        'bg-gray-100 text-gray-700',
};

function PermCell({ value }: { value: boolean | 'partiel' }) {
  if (value === true)
    return <CheckCircle2 size={20} className="text-green-500 mx-auto" />;
  if (value === 'partiel')
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold mx-auto">
        ~
      </span>
    );
  return <XCircle size={20} className="text-gray-300 mx-auto" />;
}

function OrphanReportsMigrationPanel() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const { showNotification } = useNotification();
  const [admins, setAdmins] = useState<AppUser[]>([]);
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [isMigrating, setIsMigrating] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    getDocs(collection(db, 'users')).then(snap => {
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() } as AppUser))
        .filter(u => u.role === 'admin');
      setAdmins(list);
      setSelectedAdminId(prev => prev || list[0]?.id || '');
    });
  }, [user]);

  // Réservé aux administrateurs
  if (user?.role !== 'admin') return null;

  const handleMigrate = async () => {
    if (!selectedAdminId) return;
    const adminName = admins.find(a => a.id === selectedAdminId)?.username ?? 'cet administrateur';

    const ok = await confirm({
      title: 'Attribuer les rapports sans auteur',
      description: `Tous les rapports (ADN, Finance, Académie, Sainte Cène, Sono, Culte) qui n'ont pas d'auteur seront attribués à ${adminName}. Cette action est irréversible. Continuer ?`,
      confirmLabel: 'Attribuer',
      cancelLabel: 'Annuler',
      variant: 'warning',
    });
    if (!ok) return;

    setIsMigrating(true);
    try {
      const result = await migrateOrphanReportsToAdmin(selectedAdminId);
      const detail = result.perCollection
        .filter(c => c.migrated > 0)
        .map(c => `${c.displayName}: ${c.migrated}`)
        .join(', ') || 'aucun rapport à attribuer';
      showNotification(
        'Attribution terminée',
        `${result.totalMigrated} rapport(s) attribué(s) à ${adminName}. ${detail}.`
      );
    } catch (error) {
      console.error('Erreur de migration des auteurs:', error);
      showNotification('Erreur', 'Une erreur est survenue pendant l\'attribution. Voir la console.');
    } finally {
      setIsMigrating(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-5 space-y-3">
      <div className="flex items-center gap-2 text-amber-800 font-semibold">
        <UserCheck size={20} />
        <span>Rapports sans auteur</span>
      </div>
      <p className="text-sm text-gray-600">
        Attribue à un administrateur tous les rapports qui n'ont pas encore d'auteur,
        pour qu'ils soient rattachés à un compte (les rapports créés depuis l'app ont déjà un auteur).
      </p>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <select
          value={selectedAdminId}
          onChange={e => setSelectedAdminId(e.target.value)}
          disabled={isMigrating || admins.length === 0}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00665C] text-sm"
        >
          {admins.length === 0 && <option value="">Aucun administrateur trouvé</option>}
          {admins.map(a => (
            <option key={a.id} value={a.id}>{a.username} ({a.email})</option>
          ))}
        </select>
        <button
          onClick={handleMigrate}
          disabled={isMigrating || !selectedAdminId}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] transition-colors disabled:opacity-50 text-sm"
        >
          {isMigrating ? <Loader2 size={16} className="animate-spin" /> : <UserCheck size={16} />}
          {isMigrating ? 'Attribution...' : 'Attribuer les rapports sans auteur'}
        </button>
      </div>
    </div>
  );
}

export default function RolesPage() {
  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Rôles & Permissions</h1>
        <p className="text-gray-500 mt-1 text-sm">
          Vue d'ensemble des accès accordés à chaque rôle dans l'application.
        </p>
      </div>

      <OrphanReportsMigrationPanel />

      {/* Cartes des rôles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {ROLES.map((role) => (
          <div
            key={role.key}
            className={`rounded-xl border p-4 space-y-2 ${COLOR_MAP[role.color]}`}
          >
            <div className="flex items-center space-x-2 font-semibold">
              {role.icon}
              <span>{role.label}</span>
            </div>
            <p className="text-xs leading-relaxed opacity-80">{role.description}</p>
          </div>
        ))}
      </div>

      {/* Tableau des permissions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-1/2">
                  Permission
                </th>
                {ROLES.map((role) => (
                  <th
                    key={role.key}
                    className="px-4 py-4 text-center text-xs font-semibold uppercase tracking-wider"
                  >
                    <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full ${HEADER_COLOR[role.color]}`}>
                      {role.icon}
                      <span className="hidden sm:inline">{role.label}</span>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {PERMISSIONS.map((perm, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-3 text-sm text-gray-700">{perm.label}</td>
                  <td className="px-4 py-3 text-center"><PermCell value={perm.admin} /></td>
                  <td className="px-4 py-3 text-center"><PermCell value={perm.pasteur} /></td>
                  <td className="px-4 py-3 text-center"><PermCell value={perm.responsable} /></td>
                  <td className="px-4 py-3 text-center"><PermCell value={perm.user} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Légende */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-wrap gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-green-500" /> Autorisé</span>
          <span className="flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 text-amber-700 font-bold text-[10px]">~</span>
            Partiel (selon les départements assignés)
          </span>
          <span className="flex items-center gap-1.5"><XCircle size={14} className="text-gray-300" /> Non autorisé</span>
        </div>
      </div>

      <p className="text-xs text-gray-400 text-center">
        Les permissions sont appliquées côté serveur (Firestore) et côté client. Pour toute modification des rôles, contactez l'administrateur système.
      </p>
    </div>
  );
}
